import {
  intranetContentDetailSchema,
  intranetFeedbackNotesResponseSchema,
  intranetFileListResponseSchema,
  intranetFileSchema,
  intranetNoticeListSchema,
  intranetNoticeSchema,
  type SubmitIntranetFeedbackRequest,
  type SubmitIntranetNoticeRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import {
  intranetContentKey,
  intranetContentsKey,
} from '@/products/intranet/features/intranet/use-intranet';

const okSchema = z.object({ ok: z.literal(true) });

function filesKey(slug: string) {
  return [...intranetContentKey(slug), 'files'] as const;
}

function feedbackNotesKey(slug: string) {
  return [...intranetContentKey(slug), 'feedback-notes'] as const;
}

function noticesKey(slug: string) {
  return [...intranetContentKey(slug), 'notices'] as const;
}

export function useIntranetFiles(slug: string | undefined, refetchInterval?: number | false) {
  return useQuery({
    queryKey: filesKey(slug ?? ''),
    queryFn: () =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug ?? '')}/files`,
        intranetFileListResponseSchema,
      ),
    enabled: Boolean(slug),
    refetchInterval,
  });
}

/** Same file endpoint the library version list uses. */
export function uploadIntranetFile(slug: string, file: File, replacesFileId?: string) {
  const body = new FormData();
  body.append('file', file);
  const replaces = replacesFileId ? `?replaces=${encodeURIComponent(replacesFileId)}` : '';
  return apiFetch(
    `/intranet/contents/${encodeURIComponent(slug)}/files${replaces}`,
    intranetFileSchema,
    {
      method: 'POST',
      body,
    },
  );
}

/** Upload refreshes the version list and every list/detail that shows the current file. */
export function useUploadIntranetFile(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, replacesFileId }: { file: File; replacesFileId?: string }) =>
      uploadIntranetFile(slug, file, replacesFileId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: intranetContentsKey }),
        queryClient.invalidateQueries({ queryKey: filesKey(slug) }),
        queryClient.invalidateQueries({ queryKey: intranetContentKey(slug) }),
      ]);
    },
  });
}

export function useDeleteIntranetFile(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/files/${encodeURIComponent(fileId)}`,
        okSchema,
        { method: 'DELETE' },
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: intranetContentsKey }),
        queryClient.invalidateQueries({ queryKey: filesKey(slug) }),
        queryClient.invalidateQueries({ queryKey: intranetContentKey(slug) }),
      ]);
    },
  });
}

export function useSubmitIntranetFeedback(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitIntranetFeedbackRequest) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/feedback`,
        intranetContentDetailSchema,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: async (detail) => {
      queryClient.setQueryData(intranetContentKey(slug), detail);
      await queryClient.invalidateQueries({ queryKey: feedbackNotesKey(slug) });
    },
  });
}

export function useSubmitIntranetNotice(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitIntranetNoticeRequest) =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug)}/notices`, intranetNoticeSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: noticesKey(slug) });
    },
  });
}

/** Editors only: every Meldung on the page, newest first. */
export function useIntranetNotices(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: noticesKey(slug),
    queryFn: () =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug)}/notices`, intranetNoticeListSchema),
    enabled,
  });
}

/** Editors only: vote totals and notes left for the editorial team. */
export function useIntranetFeedbackNotes(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: feedbackNotesKey(slug),
    queryFn: () =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/feedback`,
        intranetFeedbackNotesResponseSchema,
      ),
    enabled,
  });
}
